import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { orgsApi } from '../api/orgs'
import { RefusedError } from '../api/client'
import { useLandscape } from '../hooks/useLandscape'
import { ORGS_KEY, useOrg } from '../hooks/useOrg'
import { useSpoke } from '../hooks/useSpoke'
import { mailboxWords, orgCountWords, selectionWords } from '../lib/orgs'
import type { OrgView, UnassignedPerson } from '../types/orgs'

// The org switcher, at the top of the sidebar: whose pipeline you are looking at, and how to look at
// someone else's. Every mailbox domain is an org; you open on yours; anyone may switch or summarise
// every org together (docs/plans/orgs-2026-09-13.md). The role does not decide what you can see — it
// decides who may place a person in an org, which is the one thing under here an admin does.
//
// Presentation only: the counts, the names and who is in each org all come from the API. When nothing
// places the caller in an org the control says "No org yet" rather than inventing one, and the
// landscape's step card — whose words are the API's — is where the explanation lives.

/** What the control says on a screen that is only ever the signed-in person's. */
export const PERSONAL_LABEL = 'Your own — not by org'
export const PERSONAL_SUB = 'this screen is only ever yours'
export const PERSONAL_TITLE =
  'This screen is yours alone. Your mailbox, your voice and your drafts are never read for anyone else, so the org you are looking at does not change what is below.'

export function OrgSwitcher({ personal = false }: { personal?: boolean } = {}) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const { org, setOrg } = useOrg()
  const [open, setOpen] = useState(false)
  const [placing, setPlacing] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const q = useQuery({ queryKey: [ORGS_KEY, spoke], queryFn: () => orgsApi.list(spoke), staleTime: 60_000 })
  const landscape = useLandscape()
  const isAdmin = landscape.data?.viewer.isAdmin ?? false

  const place = useMutation({
    mutationFn: (to: { userId: string; key: string }) => orgsApi.place(spoke, to.userId, to.key),
    onSuccess: () => {
      setPlacing(null)
      void qc.invalidateQueries({ queryKey: [ORGS_KEY, spoke] })
      void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
    },
  })

  useEffect(() => {
    if (!open) return
    const away = (ev: MouseEvent) => { if (box.current && !box.current.contains(ev.target as Node)) setOpen(false) }
    const escape = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', escape) }
  }, [open])

  // An owner-only screen cannot answer for an org, so the control says so plainly and does nothing
  // rather than sitting above the page implying the selection applies (REQ-064-AC2). It stays in
  // place — removing it would move everything below it as you walk between screens.
  if (personal) return <PersonalScope />

  // Nothing to switch between until the API has answered: no control rather than an empty one.
  if (!q.isSuccess) return null
  const { orgs, mine, unassigned } = q.data
  const label = selectionWords(org, orgs, mine)
  const here = org === 'mine' ? orgs.find((o) => o.key === mine) : orgs.find((o) => o.key === org)
  const sub = org === 'all'
    ? `${orgs.length} ${orgs.length === 1 ? 'org' : 'orgs'}, summarised`
    : here
      ? `${orgCountWords(here)}${here.key === mine ? ' · yours' : ''}`
      : 'nothing places you in one yet'

  return (
    <div className="org-switch" ref={box}>
      <button type="button" className="org-control" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        title="Whose pipeline you are looking at. Every mailbox domain is an org; you can look at any of them, or at all of them together.">
        <span className="org-label">{label}</span>
        <span className="org-sub">{sub}</span>
      </button>
      {open && (
        <OrgMenu
          orgs={orgs}
          mine={mine}
          unassigned={unassigned}
          selection={org}
          isAdmin={isAdmin}
          placing={placing}
          pending={place.isPending ? place.variables?.userId ?? null : null}
          error={place.isError ? (place.error instanceof RefusedError ? place.error.refusal.message : place.error.message) : null}
          onPick={(next) => { setOrg(next); setOpen(false) }}
          onPlacing={setPlacing}
          onPlace={(userId, key) => place.mutate({ userId, key })}
        />
      )}
    </div>
  )
}

/** The switcher on an owner-only screen: present, marked, and inert. No hooks, so it renders in a test. */
export function PersonalScope() {
  return (
    <div className="org-switch personal">
      <button type="button" className="org-control personal" disabled aria-disabled="true" title={PERSONAL_TITLE}>
        <span className="org-label">{PERSONAL_LABEL}</span>
        <span className="org-sub">{PERSONAL_SUB}</span>
      </button>
    </div>
  )
}

export interface OrgMenuProps {
  orgs: OrgView[]
  mine: string | null
  unassigned: UnassignedPerson[]
  selection: string
  /** an admin may place a person in an org; everyone may look at any org */
  isAdmin: boolean
  /** the person whose placer is open, if any */
  placing: string | null
  /** the person whose placement is in flight */
  pending: string | null
  error: string | null
  onPick: (selection: string) => void
  onPlacing: (userId: string | null) => void
  onPlace: (userId: string, key: string) => void
}

/** The open menu. No hooks and no data of its own, so the whole of it can be rendered in a test. */
export function OrgMenu(props: OrgMenuProps) {
  const { orgs, mine, unassigned, selection, isAdmin, placing, pending, error, onPick, onPlacing, onPlace } = props
  const chosen = (key: string) => selection === key || (selection === 'mine' && key === mine)

  return (
    <div className="org-menu">
      <p className="org-menu-head">Look at</p>
      {orgs.map((o) => (
        <div key={o.key} className={`org-option${chosen(o.key) ? ' on' : ''}`}>
          {/* picking your own org selects "mine", so it follows you if you are ever placed elsewhere */}
          <button type="button" className="org-pick" onClick={() => onPick(o.key === mine ? 'mine' : o.key)}>
            <span className="org-option-name">{o.name}{o.key === mine && <em className="org-yours">yours</em>}</span>
            <span className="org-option-sub">{orgCountWords(o)} · {mailboxWords(o)}</span>
          </button>
          {isAdmin && o.members.length > 0 && (
            <ul className="org-members">
              {o.members.map((m) => (
                <li key={m.userId}>
                  <span className="org-member-name">{m.displayName}</span>
                  {!m.hasMailbox && <span className="org-member-note">no mailbox</span>}
                  <Placer userId={m.userId} label="Move" current={o.key} orgs={orgs} placing={placing} pending={pending}
                    onPlacing={onPlacing} onPlace={onPlace} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      <div className={`org-option${selection === 'all' ? ' on' : ''}`}>
        <button type="button" className="org-pick" onClick={() => onPick('all')}>
          <span className="org-option-name">All orgs · summarised</span>
          <span className="org-option-sub">{orgs.length} {orgs.length === 1 ? 'org' : 'orgs'} counted together</span>
        </button>
      </div>

      {isAdmin && unassigned.length > 0 && (
        <div className="org-unplaced">
          <p className="org-menu-head">Not in an org yet</p>
          <ul className="org-members">
            {unassigned.map((p) => (
              <li key={p.userId}>
                <span className="org-member-name">{p.displayName}</span>
                <Placer userId={p.userId} label="Place" current={null} orgs={orgs} placing={placing} pending={pending}
                  onPlacing={onPlacing} onPlace={onPlace} />
              </li>
            ))}
          </ul>
          <p className="org-note">No mailbox and no sign-in domain to read, so nothing places them. They see nothing until someone does.</p>
        </div>
      )}

      {error && <p className="org-note error">{error}</p>}
    </div>
  )
}

export interface PlacerProps {
  userId: string
  label: string
  /** the org they are in now, so it is not offered back */
  current: string | null
  orgs: OrgView[]
  placing: string | null
  pending: string | null
  onPlacing: (userId: string | null) => void
  onPlace: (userId: string, key: string) => void
}

/** One press opens the orgs; one more places the person. Admins only — the caller decides that.
 *  Exported because the tenant-settings screen places people too, and there is one placer, not two. */
export function Placer({ userId, label, current, orgs, placing, pending, onPlacing, onPlace }: PlacerProps) {
  if (placing !== userId) {
    return <button type="button" className="link org-place" onClick={() => onPlacing(userId)}>{label}</button>
  }
  const choices = orgs.filter((o) => o.key !== current)
  return (
    <span className="org-place-open">
      {choices.map((o) => (
        <button key={o.key} type="button" className="button small" disabled={pending === userId} onClick={() => onPlace(userId, o.key)}>
          {o.name}
        </button>
      ))}
      <button type="button" className="link org-place" onClick={() => onPlacing(null)}>Cancel</button>
    </span>
  )
}
